import { forwardRef, FC, MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { Box, ButtonBase, Collapse, Stack, ThemeProvider, Typography } from '@mui/material';

import LogoIcon from '@/assets/images/LogoIcon.png';
import Icon from '@/components/ui/Icon';
import { IconType } from '@/assets/icons';
import { SXStyleSheet } from '@/utils';
import normalTheme from '@/theme';
import { hellFixiert, useHellGesperrt } from '@/theme/farbschema';

import style, { eintragStyle } from './style';

export * from './style';

/**
 * Gemeinsamer Rahmen von CRM-Shell und Beratungsstrecke (Nacharbeit
 * Beratungsstrecke, 27.10.2026). Werte stehen in `./style`; die Bausteine
 * tragen keine Logik — Klickziele, Aktiv-Zustand und Hover-Verhalten kommen
 * vom Aufrufer.
 *
 *   <NavyRahmen>
 *     <SidebarRahmen> Logo · Umschalter · Zurueck · Eintraege </SidebarRahmen>
 *     <Stack> <KopfBand/> <InhaltsFlaeche> Seite </InhaltsFlaeche> </Stack>
 *   </NavyRahmen>
 */

/** Navy-Flaeche hinter Sidebar, Kopfband und Rundecke. */
export const NavyRahmen: FC<PropsWithChildren> = ({ children }) => (
  <Stack direction="row" flex={1} minHeight={0} overflow="hidden" sx={style.navyRahmen}>
    {children}
  </Stack>
);

/** Das 48px-Band ueber dem Inhalt. */
export const KopfBand: FC<PropsWithChildren> = ({ children }) => (
  <Stack sx={style.band}>{children}</Stack>
);

/** Inhaltsflaeche mit der 16px-Rundecke oben links zum Navy-Rahmen. */
export const InhaltsFlaeche: FC<PropsWithChildren> = ({ children }) => (
  <Stack flex={1} minHeight={0} position="relative" sx={style.inhaltsflaeche}>
    {children}
  </Stack>
);

export interface SidebarRahmenProps {
  ausgeklappt: boolean;
  /** Name der Navigations-Landmarke. */
  ariaLabel: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}

/**
 * Sidebar-Spalte: 216px auf, 64px zu, 8px Innenrand in beiden Zustaenden.
 *
 * Steht immer im normalen Theme, auch im Kompakt-Modus (Decision #247): die
 * Breite ist fest, kleinere Abstaende gewinnen dort nichts und wuerden die
 * Icons gegen das Logo (fester Rand) verschieben.
 */
export const SidebarRahmen = forwardRef<HTMLDivElement, SidebarRahmenProps>(
  ({ ausgeklappt, ariaLabel, onMouseEnter, onMouseLeave, children }, ref) => {
    // In der Strecke (Hell-Sperre, Decision #248) die helle Palette auch in JS.
    const hellGesperrt = useHellGesperrt();
    return (
      <ThemeProvider theme={hellGesperrt ? hellFixiert(normalTheme) : normalTheme}>
        <Stack
          ref={ref}
          sx={SXStyleSheet.merge(
            style.sidebar,
            ausgeklappt ? style.sidebarAusgeklappt : style.sidebarEingeklappt,
          )}
          role="navigation"
          aria-label={ariaLabel}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {children}
        </Stack>
      </ThemeProvider>
    );
  },
);
SidebarRahmen.displayName = 'SidebarRahmen';

/** Logo 32px auf der Icon-Achse, ausgeklappt mit Firmenname daneben. */
export const SidebarLogo: FC<{ ausgeklappt: boolean }> = ({ ausgeklappt }) => (
  <Stack sx={style.brandLockup}>
    <Box component="img" src={LogoIcon} alt="DIF Logo" sx={style.logo} />
    {ausgeklappt && (
      <Typography sx={style.companyName}>
        Deutsche Investitions- und Förderberatung AG
      </Typography>
    )}
  </Stack>
);

/** Auf-/Zuklappen der Sidebar. */
export const SidebarUmschalter: FC<{ ausgeklappt: boolean; onClick: () => void }> = ({
  ausgeklappt,
  onClick,
}) => (
  <ButtonBase
    sx={SXStyleSheet.merge(
      style.toggleButton,
      !ausgeklappt && style.toggleButtonCollapsed,
    )}
    onClick={onClick}
    disableRipple
    aria-label={ausgeklappt ? 'Sidebar einklappen' : 'Sidebar ausklappen'}
    aria-expanded={ausgeklappt}
  >
    <Icon
      icon="Next"
      size={20}
      sx={SXStyleSheet.merge(
        { color: 'navigation.text' },
        ausgeklappt && style.toggleIconExpanded,
      )}
    />
  </ButtonBase>
);

export interface SidebarZurueckProps {
  ausgeklappt: boolean;
  icon: IconType;
  label: string;
  onClick: () => void;
  'data-testid'?: string;
}

/** Zurueck-Knopf, Icon auf derselben Achse wie die Eintraege. */
export const SidebarZurueck: FC<SidebarZurueckProps> = ({
  ausgeklappt,
  icon,
  label,
  onClick,
  'data-testid': testId,
}) => (
  <ButtonBase sx={style.backButton} onClick={onClick} disableRipple data-testid={testId}>
    <Icon icon={icon} size={20} sx={{ color: 'navigation.text' }} />
    {ausgeklappt && (
      <Typography variant="wertBetont" sx={style.backButtonLabel}>
        {label}
      </Typography>
    )}
  </ButtonBase>
);

export interface SidebarPfeilProps {
  offen: boolean;
  onClick: (e: MouseEvent) => void;
  ariaLabel?: string;
}

/**
 * Pfeil einer Gruppe mit Unterpunkten: klappt nur auf und zu. Eigenes
 * Klickziel innerhalb des Eintrags, deshalb als `span` (kein Knopf im Knopf).
 */
export const SidebarPfeil: FC<SidebarPfeilProps> = ({ offen, onClick, ariaLabel }) => (
  <ButtonBase
    component="span"
    disableRipple
    onClick={(e: MouseEvent) => {
      e.stopPropagation();
      onClick(e);
    }}
    aria-label={ariaLabel}
    aria-expanded={offen}
    sx={eintragStyle.chevronButton}
  >
    <Icon
      icon="DropdownArrow"
      size={16}
      sx={SXStyleSheet.merge(
        eintragStyle.chevronIcon,
        offen && eintragStyle.chevronIconExpanded,
      )}
    />
  </ButtonBase>
);

export interface SidebarEintragProps {
  icon?: IconType;
  label?: string;
  aktiv?: boolean;
  ausgeklappt?: boolean;
  altColor?: boolean;
  onClick: () => void;
  onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: () => void;
  /** Pfeil rechts (nur ausgeklappt sinnvoll). */
  pfeil?: ReactNode;
  /** Unterpunkte unter der Zeile (`SidebarUnterpunkte`). */
  children?: ReactNode;
}

/**
 * Ein Sidebar-Eintrag: Icon an fester Stelle (x 22 in beiden Zustaenden),
 * Beschriftung nur ausgeklappt.
 */
export const SidebarEintrag: FC<SidebarEintragProps> = ({
  icon,
  label,
  aktiv,
  ausgeklappt,
  altColor,
  onClick,
  onMouseEnter,
  onMouseLeave,
  pfeil,
  children,
}) => (
  <Stack mb={1}>
    <Stack
      sx={SXStyleSheet.merge(
        eintragStyle.container,
        aktiv && eintragStyle.containerActive,
        altColor && eintragStyle.containerAltColor,
        altColor && aktiv && eintragStyle.altColorActive,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ButtonBase
        sx={SXStyleSheet.merge(
          eintragStyle.buttonContainer,
          aktiv && eintragStyle.buttonContainerActive,
        )}
        disableRipple={true}
        onClick={onClick}
      >
        {!!icon && <Icon icon={icon} size={20} />}
        {ausgeklappt && label && (
          <Typography
            variant="wertBetont"
            sx={SXStyleSheet.merge(
              eintragStyle.labelText,
              aktiv && eintragStyle.labelTextActive,
            )}
          >
            {label}
          </Typography>
        )}
        {ausgeklappt && pfeil}
      </ButtonBase>
    </Stack>
    {children}
  </Stack>
);

/** Aufklappbare Liste der Unterpunkte unter einem Eintrag. */
export const SidebarUnterpunkte: FC<PropsWithChildren<{ offen: boolean }>> = ({
  offen,
  children,
}) => (
  <Collapse in={offen} timeout={200}>
    <Stack sx={eintragStyle.subItemsContainer} mt={0.5} mb={1}>
      {children}
    </Stack>
  </Collapse>
);

export interface SidebarUnterpunktProps {
  aktiv?: boolean;
  onClick: () => void;
  children: ReactNode;
}

export const SidebarUnterpunkt: FC<SidebarUnterpunktProps> = ({ aktiv, onClick, children }) => (
  <ButtonBase
    disableRipple
    onClick={onClick}
    sx={SXStyleSheet.merge(
      eintragStyle.subItemButton,
      aktiv && eintragStyle.subItemActive,
    )}
  >
    {children}
  </ButtonBase>
);
